"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";
import { cn } from "@/lib/utils";

type SenderRole = "user" | "admin";

interface ChatMessage {
  id: string;
  conversationId: string;
  senderRole: SenderRole;
  body: string;
  createdAt: string;
  readAt: string | null;
}

interface ChatConversation {
  id: string;
  status: "open" | "closed";
}

const FOUNDER_NAME = "Kai";
const OPENING_MESSAGE = `Hi, I'm ${FOUNDER_NAME}, founder of Foundry. I read every message personally — usually within a few hours, sometimes by next morning. What's up?`;

function isWorkingHoursET(): boolean {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "numeric",
      hour12: false,
    }).format(new Date())
  );
  return hour >= 9 && hour < 22;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function SupportChat() {
  const [open, setOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [workingHours, setWorkingHours] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  useEffect(() => {
    setWorkingHours(isWorkingHoursET());
    const interval = setInterval(() => setWorkingHours(isWorkingHoursET()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const refetch = useCallback(async () => {
    const res = await fetch("/api/chat/messages", { credentials: "include" });
    if (!res.ok) return;
    const data = (await res.json()) as { conversation: ChatConversation; messages: ChatMessage[] };
    setConversationId(data.conversation.id);
    setMessages(data.messages);
  }, []);

  useEffect(() => {
    if (!open) return;
    void refetch();
    void fetch("/api/chat/read", { method: "POST", credentials: "include" }).catch(() => undefined);
    setUnread(0);
  }, [open, refetch]);

  useEffect(() => {
    if (!open) return;
    const es = new EventSource("/api/chat/stream", { withCredentials: true });
    eventSourceRef.current = es;

    es.addEventListener("message", () => {
      void refetch();
    });
    es.onerror = () => {
      // EventSource auto-reconnects on transient errors
    };

    return () => {
      es.close();
      eventSourceRef.current = null;
    };
  }, [open, refetch]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  // Background unread polling when widget closed (cheap — once per minute)
  useEffect(() => {
    if (open) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await fetch("/api/chat/messages", { credentials: "include" });
        if (!res.ok || cancelled) return;
        const data = (await res.json()) as { messages: ChatMessage[] };
        const u = data.messages.filter((m) => m.senderRole === "admin" && !m.readAt).length;
        setUnread(u);
      // intentional: 60s background unread poll; transient failure recovers on next tick
      } catch {
        // ignore
      }
    };
    void tick();
    const interval = setInterval(tick, 60_000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [open]);

  const handleSend = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      const body = input.trim();
      if (!body || sending) return;
      setSending(true);
      const optimistic: ChatMessage = {
        id: `tmp-${Date.now()}`,
        conversationId: conversationId ?? "",
        senderRole: "user",
        body,
        createdAt: new Date().toISOString(),
        readAt: null,
      };
      setMessages((prev) => [...prev, optimistic]);
      setInput("");
      try {
        const res = await fetch("/api/chat/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ body }),
        });
        if (res.ok) {
          await refetch();
        } else {
          setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
          setInput(body);
        }
      } catch {
        setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
        setInput(body);
      } finally {
        setSending(false);
      }
    },
    [input, sending, conversationId, refetch]
  );

  const slaBadge = useMemo(
    () =>
      workingHours
        ? "Typically replies in a few hours"
        : "Typically replies within 24 hours",
    [workingHours]
  );

  const showOpening = messages.length === 0;

  return (
    <>
      {!open && (
        <button
          type="button"
          data-no-hover-shine
          onClick={() => setOpen(true)}
          className={cn(
            "fixed bottom-5 right-5 z-50 flex h-12 w-12 items-center justify-center rounded-full",
            "bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          )}
          aria-label="Open support chat"
        >
          <MessageCircle className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
              {unread}
            </span>
          )}
        </button>
      )}

      {open && (
        <div
          className={cn(
            "fixed bottom-5 right-5 z-50 flex flex-col w-[min(380px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-2.5rem))]",
            "rounded-xl border border-border bg-background shadow-2xl overflow-hidden"
          )}
        >
          <header className="flex items-start gap-3 border-b border-border bg-muted/40 px-4 py-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
              K
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-foreground truncate">{FOUNDER_NAME}</div>
              <div className="text-[11px] text-muted-foreground truncate">{slaBadge}</div>
            </div>
            <button
              type="button"
              data-no-hover-shine
              onClick={() => setOpen(false)}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Close support chat"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {showOpening && (
              <MessageBubble role="admin" body={OPENING_MESSAGE} time={null} />
            )}
            {messages.map((m) => (
              <MessageBubble
                key={m.id}
                role={m.senderRole}
                body={m.body}
                time={formatTime(m.createdAt)}
              />
            ))}
          </div>

          <form
            onSubmit={handleSend}
            className="flex items-end gap-2 border-t border-border bg-background p-2"
          >
            <textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void handleSend();
                }
              }}
              rows={1}
              placeholder="Bug, question, idea…"
              className={cn(
                "flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm",
                "placeholder:text-muted-foreground/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
                "max-h-32"
              )}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className={cn(
                "flex h-9 w-9 items-center justify-center rounded-md bg-primary text-primary-foreground",
                "hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed"
              )}
              aria-label="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function MessageBubble({
  role,
  body,
  time,
}: {
  role: SenderRole;
  body: string;
  time: string | null;
}) {
  const isAdmin = role === "admin";
  return (
    <div className={cn("flex flex-col", isAdmin ? "items-start" : "items-end")}>
      <div
        className={cn(
          "max-w-[80%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words",
          isAdmin
            ? "bg-muted text-foreground rounded-bl-sm"
            : "bg-primary text-primary-foreground rounded-br-sm"
        )}
      >
        {body}
      </div>
      {time && (
        <span className="mt-1 text-[10px] text-muted-foreground/70">{time}</span>
      )}
    </div>
  );
}
